<template>
	<VPopup
		ref="popup"
		position="bottom"
		align="right"
		:offset="21"
		show-pointer
		:visible="true"
		:target="target"
    :close="$core.dialogs.OrderPositionShipmentsPopup.hide"
	>
		<VDataGridV2
			layout="fill"
			:data="groupedData"
			:loading="loading"
			group-children-property="children"
		>
			<VDataGridGroupV2>
				<template #default="{ parent }">
					<div>
            {{ parent.qtyTotal?.format(parent.unit) }}
          </div>
				</template>
			</VDataGridGroupV2>

			<VDataGridColumnV2
				id="id"
				label="№"
				width="50px"
        content-class="nowrap"
			>
				<template #default="{ row }">
          <VLink @click="showEntity(row)">{{ $core.utils.string.formatWithDelimiter(row.id) }}</VLink>
					<div class="description">
						{{ $core.utils.date.format(row.createdAt) }}
					</div>
				</template>
			</VDataGridColumnV2>

			<VDataGridColumnV2
				id="type"
				label="Тип"
				width="100px"
        content-class="nowrap"
			>
				<template #default="{ row }">
					<template v-if="row.name === 'shipment'">
						<div>{{ row.nameRu }}</div>
						<div class="description">
							{{ row.nameSecondary }}
						</div>
					</template>
					<template v-else>
						{{ row.nameRu }}
					</template>
				</template>
			</VDataGridColumnV2>

			<VDataGridColumnV2
				id="qty"
				label="Кол-во"
				content-align="right"
				width="110px"
        content-class="nowrap"
			>
				<template #default="{ row }">
					<div class="nowrap">
						{{ row.qty?.format(row.unit) }}
					</div>
					<div
						v-if="row.qtySecondary"
						class="description nowrap"
						title="Недостача"
					>
						{{ row.qtySecondary?.format(row.unit) }}
					</div>
				</template>
			</VDataGridColumnV2>

			<VDataGridColumnV2
				id="date"
				label="Дата"
				content-align="center"
				width="100px"
			>
				<template #default="{ row }">
					<div>
						{{ $core.utils.date.format(row.date) }}
					</div>
					<div class="description">
						{{ row.dateDescription }}
					</div>
				</template>
			</VDataGridColumnV2>
		</VDataGridV2>

    <!-- блок и стили вынужденное зло -->

		<footer
			v-if="entities?.length"
			class="shipment-status-section"
		>
			<div class="status-item">
				<div class="status-dot assembled"/>
				<span class="status-label">Собрано:</span>
				<span class="status-value">{{ statusTotals.qtyAssembled?.format(position.unit) }}</span>
			</div>
			<div class="status-item">
				<div class="status-dot shipped"/>
				<span class="status-label">Отгружено:</span>
				<span class="status-value">{{ statusTotals.qtyShipped?.format(position.unit) }}</span>
			</div>
			<div class="status-item">
				<div class="status-dot received"/>
				<span class="status-label">Принято:</span>
				<span class="status-value">{{ statusTotals.qtyReceived?.format(position.unit) }}</span>
			</div>
			<div class="status-item">
				<div class="status-dot substandard"/>
				<span class="status-label">Брак:</span>
				<span class="status-value">{{ statusTotals.qtyComplained?.format(position.unit) }}</span>
			</div>
		</footer>
	</VPopup>
</template>

<style lang="scss" src="./OrderPositionShipmentsPopup.scss"/>

<script lang="ts">

import type OrderPosition            from '@/domains/Orders/OrderPosition';
import OrdersService                 from '@/domains/Orders/OrdersService';
import Core                          from "@/core/Core";
import OrderPositionShipmentTranche  from '@/domains/Orders/components/OrderPositionShipmentsPopup/OrderPositionShipmentTranche';
import OrderPositionShipmentTrancheRecord from '@/domains/Orders/components/OrderPositionShipmentsPopup/OrderPositionShipmentTrancheRecord';

export default defineComponent({
	name: 'OrderPositionShipmentsPopup',

	props: {
		position: {
			type:    Object as PropType<OrderPosition>,
			default: null,
		},
		target: {
			type:    HTMLElement,
			default: null,
		},
	},

	data() {
		return {
			entities: [] as OrderPositionShipmentTranche[],
			loading:  false,
		};
	},

	async mounted() {
		await this.init();
	},

	computed: {
		groupedData(): any[] {
			return this.entities.map((tranche, index) => ({
				id:           index + 1,
				qtyAssembled: tranche.qtyAssembled,
				qtyReceived:  tranche.qtyReceived,
				qtyShipped:   tranche.qtyShipped,
				qtyTotal:     tranche.qtyTotal,
				unit:         tranche.unit,
				children:     tranche.entities.map((entity: OrderPositionShipmentTrancheRecord) => ({
					...entity,
					unit: tranche.unit,
				})),
			}));
		},

		statusTotals() {
			const shipmentStatus = this.position?.shipmentStatus ?? null;
			return {
				qtyAssembled:  shipmentStatus?.assembled,
				qtyShipped:    shipmentStatus?.shipped,
				qtyReceived:   shipmentStatus?.receivedByBuyer,
				qtyComplained: shipmentStatus?.complainedSubstandard,
			};
		},
	},

	methods: {
		async init(): Promise<void> {
			this.loading  = true;
			this.entities = await OrdersService.getPositionShipmentDetails(this.position);
			this.loading  = false;

			await this.$nextTick();
			(this.$refs.popup as any).calculatePosition();
		},

    /**
     * Карточки открываются диалогами напрямую: в попапе лежат сырые данные
     * от getPositionShipmentDetails, доменных сущностей здесь нет — только
     * тип и id. Сервисные showXxxCard принимают сущность, а грузить документ
     * ради того, чтобы карточка загрузила его повторно, незачем.
     */
    showEntity(entity: any): void {
      if (entity.name === 'shipment') {
        Core.dialogs.ShipmentCard.show({ id: entity.id });
      }
      else if (entity.name === 'assembly') {
        Core.dialogs.AssemblyCard.show({ id: entity.id });
      }
      else if (entity.name === 'receipt') {
        Core.dialogs.ReceiptCard.show({ id: entity.id });
      }

      this.$core.dialogs.OrderPositionShipmentsPopup.hide();
    },
	},
});
</script>